import { useMemo } from 'react';
import type Highcharts from 'highcharts';
import type { ReadingsByStage, DirectShearReading } from '../../../../types/directShearReading';
import type { AnalysisData } from '../analysisTypes';
import { baseChartOptions, STAGE_COLORS, STAGE_MARKERS, CHART_COLORS } from '../chartConfig';
import { symmetricAxisBounds } from '../../../../utils/chartUtils';
import { isResultReading } from '../../../../utils/directShearReadings';

export interface MultiStageOverlayChartProps {
  readingsByStage: ReadingsByStage;
  analysisByStage: Record<number, AnalysisData>;
  yField: keyof DirectShearReading;
  xField?: keyof DirectShearReading;
  title: string;
  yLabel: string;
  xLabel?: string;
  height?: number;
  yMin?: number;
}

// BUG-2026-0199 #3: peak dots on the summary plots used to be re-derived here
// from the raw readings (max CorrectedFrictionAngle in the in-memory range),
// independently of the STORED, table-backed peak. Combined with the old 0–1
// auto-default that meant dots appeared for stages the table showed nothing
// for, and at positions that didn't match the "Peak & Residual Summary" table.
// The dots are now sourced from the same stored values the table renders, so
// they line up exactly and only appear once a stage has a computed peak.
//
// Only the fields the summary table actually itemises get a dot; the Vertical
// Displacement and Dilation Angle overlays are intentionally left un-dotted
// (the table has no peak row for them, so a dot there had "no correlation").
const PEAK_Y_STORED: Partial<Record<keyof DirectShearReading, keyof AnalysisData>> = {
  CorrectedShearStress: 'peakShearStress',
  CorrectedFrictionAngle: 'peakFrictionAngle',
};

/**
 * Build Highcharts `zones` for ONE stage's line so segments inside that
 * stage's peak range render yellow and segments inside its residual range
 * render red. Segments outside either range fall back to the stage's own
 * color (no `color` override), so stage-to-stage distinguishability is
 * preserved on the multi-stage overlay.
 *
 * Only applied when xField is HorizontalDisplacementCumulative — the range
 * bounds are expressed in horizontal displacement, so they don't translate
 * to the Mohr-Coulomb (normal-stress-vs-shear-stress) chart.
 */
function buildStageZones(
  ranges: {
    peakRangeFrom: number | null;
    peakRangeTo: number | null;
    residualRangeFrom: number | null;
    residualRangeTo: number | null;
  },
): Highcharts.SeriesZonesOptionsObject[] | undefined {
  const hasPeak = ranges.peakRangeFrom != null && ranges.peakRangeTo != null;
  const hasResidual = ranges.residualRangeFrom != null && ranges.residualRangeTo != null;
  if (!hasPeak && !hasResidual) return undefined;

  // Collect each range and sort by start X so the zones array is in order.
  type Range = { from: number; to: number; color: string };
  const rangeList: Range[] = [];
  if (hasPeak) rangeList.push({ from: ranges.peakRangeFrom!, to: ranges.peakRangeTo!, color: CHART_COLORS.yellow });
  if (hasResidual) rangeList.push({ from: ranges.residualRangeFrom!, to: ranges.residualRangeTo!, color: CHART_COLORS.red });
  rangeList.sort((a, b) => a.from - b.from);

  const zones: Highcharts.SeriesZonesOptionsObject[] = [];
  for (const r of rangeList) {
    zones.push({ value: r.from });           // up to range start → stage color (no override)
    zones.push({ value: r.to, color: r.color }); // inside range → highlight color
  }
  zones.push({});                             // after the last range → stage color again
  return zones;
}

export function useMultiStageOverlayOptions({
  readingsByStage,
  analysisByStage,
  yField,
  xField = 'HorizontalDisplacementCumulative',
  title,
  yLabel,
  xLabel,
  height,
  yMin,
}: MultiStageOverlayChartProps): Highcharts.Options {
  return useMemo((): Highcharts.Options => {
    const stageNums = Object.keys(readingsByStage)
      .map(Number)
      .filter((n) => readingsByStage[n]?.length > 0)
      .sort();

    // Range highlighting only makes sense on X = Horizontal Displacement
    // (that's what the peak/residual range bounds are expressed in).
    const canUseRangeZones = xField === 'HorizontalDisplacementCumulative';

    const series: Highcharts.SeriesOptionsType[] = stageNums.map((stageNum) => {
      const readings = readingsByStage[stageNum];
      // Only RESULT-table rows are plotted — on every overlay, including the
      // Shear-vs-Normal stress path. Seating-phase rows (IsSeating, mig 108)
      // used to be admitted on the stress path as a KeyLAB-style 0→seating
      // ramp; BUG-2026-0199 rework (Shawn Hegger, 2026-09-16) asked for the
      // curves to start at the first result row instead of the origin.
      const data = readings
        .filter(
          (r) =>
            isResultReading(r) &&
            r.CorrectedShearStress != null &&
            r[xField] != null &&
            r[yField] != null,
        )
        .map((r) => [
          r[xField] as number,
          r[yField] as number,
        ]);

      const idx = stageNum - 1;
      const useLineType = xField !== 'HorizontalDisplacementCumulative';

      // Per-stage yellow/red range highlighting — same visual treatment as
      // the per-stage chart's buildColorZones, but with the stage's own
      // color kept outside the ranges (so all 5 stages stay distinguishable).
      const a = analysisByStage[stageNum];
      const zones = canUseRangeZones && a ? buildStageZones(a) : undefined;

      return {
        type: (useLineType ? 'line' : 'spline') as 'line' | 'spline',
        name: `Stage ${stageNum}`,
        data,
        color: STAGE_COLORS[idx] ?? '#999',
        marker: { enabled: false, symbol: STAGE_MARKERS[idx] ?? 'circle' },
        lineWidth: 2.5,
        turboThreshold: 0,
        ...(zones && { zoneAxis: 'x' as const, zones }),
      };
    });

    // Peak markers — yellow-filled circles placed at the STORED (table-backed)
    // peak for each stage. X is the stored peak displacement (or peak normal
    // stress on the stress-path chart); Y is the stored peak value for this
    // chart's field. No residual marker: the range itself is the "residual
    // indicator" via red line zones above.
    const peakYStored = PEAK_Y_STORED[yField];
    const peakXStored: keyof AnalysisData =
      xField === 'CorrectedNormalStress' ? 'peakNormalStress' : 'peakHorizontalDisp';
    const peakPoints: Highcharts.PointOptionsObject[] = [];
    if (peakYStored) {
      stageNums.forEach((stageNum) => {
        const a = analysisByStage[stageNum];
        if (!a) return;
        const xv = a[peakXStored];
        const yv = a[peakYStored];
        if (xv != null && yv != null) {
          peakPoints.push({
            x: xv as number,
            y: yv as number,
            name: `Stage ${stageNum} Peak`,
          });
        }
      });
    }

    if (peakPoints.length > 0) {
      series.push({
        type: 'scatter',
        name: 'Peak',
        data: peakPoints,
        color: '#000',
        marker: { symbol: 'circle', radius: 5, fillColor: CHART_COLORS.yellow, lineColor: '#000', lineWidth: 2 },
        zIndex: 10,
      });
    }

    // Dilation only: mirror the Y axis around zero (global across all stages).
    const isDilation = yField === 'DilationAngle';
    const allYValues = series
      .filter((s: any) => s.type === 'spline' || s.type === 'line')
      .flatMap((s: any) => (s.data || []).map((d: any) => (Array.isArray(d) ? d[1] : d.y) as number))
      .filter((v: number) => v != null && isFinite(v));
    const symBounds = isDilation && allYValues.length > 0
      ? symmetricAxisBounds(allYValues)
      : undefined;

    return {
      ...baseChartOptions,
      chart: {
        ...(baseChartOptions.chart as Highcharts.ChartOptions),
        height: height ?? (baseChartOptions.chart as Highcharts.ChartOptions)?.height,
      },
      title: { text: title },
      xAxis: {
        ...(baseChartOptions.xAxis as Highcharts.XAxisOptions),
        title: { text: xLabel ?? 'Horizontal Displacement (mm)' },
        // BUG-2026-0199 #4: only pin the X axis to 0 for displacement-X charts
        // (displacement genuinely starts at 0). The Shear-vs-Normal-Stress
        // (stress-path) chart has no data at the origin, so let it auto-scale to
        // the data instead of forcing an empty gap down to (0,0).
        min: xField === 'HorizontalDisplacementCumulative' ? 0 : undefined,
      },
      yAxis: {
        ...(baseChartOptions.yAxis as Highcharts.YAxisOptions),
        title: { text: yLabel },
        min: yMin,
        ...(isDilation && symBounds && { min: symBounds.min, max: symBounds.max }),
        plotLines: isDilation ? [{
          value: 0, color: '#BDBDBD', dashStyle: 'Dash' as Highcharts.DashStyleValue,
          width: 1, zIndex: 3,
        }] : undefined,
      },
      legend: { enabled: true },
      series,
    };
  }, [readingsByStage, analysisByStage, yField, xField, title, yLabel, xLabel, height, yMin]);
}
